// src/data/countriesService.js
// 2026-09-02 country-selection foundation build
// (docs/briefs/2026-09-02-country-foundation-and-map-picker-affordance-build.md).
//
// Single seam for the two country-scoped stores this build introduces, per the brief's
// Convertibility requirement: every consumer depends on the accessor below, never on the
// literal/query directly, so a later move to a table changes only the accessor body.
//
// - The OFFERED country set (which countries have boundary data) is read live via
//   getAvailableCountries() — already data-driven (get_available_countries() RPC), just newly
//   named as its own accessor so every consumer (not only UnifiedLocationSelector) can share it.
// - The PRESENTATION defaults (map framing per country) stay a hand-maintained code file for
//   now (Michael's decision, brief §4) — row-shaped, serializable entries (no functions/JSX),
//   so a future migration is "create table, insert these rows, repoint the accessor."

import { supabaseFetch } from '../lib/supabase';
import { baseParishId } from '../utils/scopeResolution';

// The one configured fallback country — was scattered as a literal 'PT' across
// MapLocationPicker, RegisterLocationStep, and MyProfile before this build.
export const DEFAULT_COUNTRY_CODE = 'PT';

// Relocated verbatim from MapLocationPicker.jsx's former SERVED_REGION_DEFAULTS (served-region
// opening view: centre, bounds, zoom per country — distinct from UnifiedLocationSelector's own
// COUNTRY_COORDS/getMapConfig, which drives the whole-country manual-switch reframe and is out
// of this build's scope; see docs/reuse-backlog.md 2026-09-02 entry on the resulting duality).
export const COUNTRY_PRESENTATION_DEFAULTS = {
  PT: {
    // Silver Coast: Caldas da Rainha / Obidos / Nazare / Alcobaca
    center: [39.4, -9.1],
    bounds: [[39.1, -9.5], [39.75, -8.7]],
    zoom: 10
  },
  GB: {
    center: [54.5, -3.5],
    bounds: [[49.9, -8.0], [60.9, 2.0]],
    zoom: 6
  }
};

// Accessor for the presentation-defaults store — never index COUNTRY_PRESENTATION_DEFAULTS
// directly from a caller (Convertibility: the whole point is one seam to repoint later).
export function getCountryPresentationDefaults(countryCode) {
  return COUNTRY_PRESENTATION_DEFAULTS[countryCode] || COUNTRY_PRESENTATION_DEFAULTS[DEFAULT_COUNTRY_CODE];
}

// Accessor for the offered-country set — wraps get_available_countries(), already
// data-driven (distinct country_codes with boundary rows; confirmed live 2026-09-02: a clean
// {PT, GB}, no cruft codes to exclude). Returns [{country_code, country_name, boundary_count}].
export async function getAvailableCountries() {
  const { data, error } = await supabaseFetch.rpc('get_available_countries');
  if (error) throw error;
  return data || [];
}

// The default-country precedence chain (brief §2): existing-home country wins, else IP, else
// the configured fallback. Each candidate is validated against the presentation-defaults set
// before being accepted — an IP hit (or, in principle, an existing home) for a country with
// boundary data but no framing entry yet (brief §1's documented "staging before opening"
// future case) falls through instead of framing on nothing useful. That same gate also
// incidentally fails safe if get_boundaries_by_ids/get_boundaries_by_level's country_code were
// ever truncated by their un-enforced `character` (not `character(2)`) RETURNS TABLE signature
// (docs/reuse-backlog.md 2026-09-02 entry, re-flagged here as a second, more consequential
// dependent) — a truncated 'G'/'P' isn't a valid key here either, so it falls through rather
// than silently mis-framing.
export function resolveDefaultCountry({ existingHomeCountryCode = null, ipCountryCode = null } = {}) {
  const candidates = [
    ['existing-home', existingHomeCountryCode],
    ['IP', ipCountryCode]
  ];
  for (const [source, candidate] of candidates) {
    const normalized = candidate?.trim()?.toUpperCase();
    if (!normalized) continue;
    if (COUNTRY_PRESENTATION_DEFAULTS[normalized]) return normalized;
    console.warn(`resolveDefaultCountry: ${source} country "${normalized}" has no presentation defaults yet — falling through`);
  }
  return DEFAULT_COUNTRY_CODE;
}

// 2026-09-02 unit-label-per-country build
// (docs/briefs/2026-09-02-unit-label-per-country-and-country-selector-reorder-build.md).
//
// The crosshair's base unit — the finest is_user_selectable level_level_config row per country
// (confirmed live via resolve_point_to_boundary(): PT resolves to admin_level 8, GB to
// admin_level 9 — both the finest is_user_selectable=true row for their country; GB also has a
// coarser selectable row at level 4/Local Authority, so "finest" is load-bearing, not
// "the only selectable row"). unit_label is place data shown verbatim — "freguesia"/"ward" —
// never translated, distinct from level_label/level_label_pt's generic tier names.
//
// Read directly via supabaseFetch.query (public SELECT confirmed on location_level_config —
// verified against pg_policy directly: the "Public can read location level config" policy has
// polroles={0} (PUBLIC, not scoped to `authenticated`) and using_expr='true', not inferred from
// get_available_levels()'s SECURITY DEFINER behaviour, which would bypass RLS and prove nothing
// about anon access — both this build's target flows (registration, guest quick-picker) are
// unauthenticated).
export async function getBaseUnitLabel(countryCode) {
  const { data, error } = await supabaseFetch.query('location_level_config', {
    select: 'admin_level,unit_label',
    eq: { country_code: countryCode, is_user_selectable: true }
  });
  if (error || !data?.length) return null;
  const finest = data.reduce((a, b) => (b.admin_level > a.admin_level ? b : a));
  return finest.unit_label || null;
}

// Roadmap #85 (docs/briefs/2026-10-01-country-default-language-addendum-1.md, A3): the country in
// view is the country of a place, read here once for everyone — the header (which also shows the
// place's name) and App.js's country-in-view derivation ask for the same row, so successful reads
// are kept for the life of the page (a boundary's name and country do not change under it). A
// failed read is not kept, so the next caller retries.
const boundaryPlaceCache = new Map();

/**
 * A boundary's name and country.
 * @param {number|string} boundaryId
 * @returns {Promise<{ name: string|null, countryCode: string|null } | null>} null when the read failed
 */
export function getBoundaryPlace(boundaryId) {
  if (boundaryId == null || boundaryId === '') return Promise.resolve(null);
  const key = Number(boundaryId);
  if (boundaryPlaceCache.has(key)) return boundaryPlaceCache.get(key);

  const pending = supabaseFetch.query('admin_boundaries', {
    select: 'name,country_code',
    eq: { id: key },
    single: true
  }).then(({ data, error }) => {
    if (error || !data) {
      boundaryPlaceCache.delete(key);
      return null;
    }
    return { name: data.name || null, countryCode: data.country_code?.trim() || null };
  }, () => {
    boundaryPlaceCache.delete(key);
    return null;
  });
  boundaryPlaceCache.set(key, pending);
  return pending;
}

/**
 * The country of a member's area: the country of its base parish (baseParishId — the one rule for
 * "which parish is this member's"). An area object without its boundaries (some App.js paths hold
 * only id, area_code and display_name) is read first.
 * @param {object} area - a user_areas row, or at least { id }
 * @returns {Promise<string|null>} null when unknown
 */
export async function getAreaCountryCode(area) {
  if (!area) return null;
  let parishId = baseParishId(area);
  if (!parishId && area.id) {
    const { data, error } = await supabaseFetch.query('user_areas', {
      select: 'primary_boundary_id,boundary_ids',
      eq: { id: area.id },
      single: true
    });
    if (error || !data) return null;
    parishId = baseParishId(data);
  }
  const place = await getBoundaryPlace(parishId);
  return place?.countryCode || null;
}
